// utils/photoPairs.js — which of an item's photos is a fix and which it answers.
// ---------------------------------------------------------------------------
// v0.317.0 (2026-11-03). The InSpec field app's ghost camera takes the fix
// photo through the old one, from the same spot. The server keeps it as
// kind 'after' with beforePhotoId naming the photo it answers
// (api/shared/photo-pair.js). These read the pair back for the screens.
//
// A pair is shown only when BOTH photos are in the list the screen has: an
// after whose before is not there (removed, or a conversation photo) is an
// ordinary photo. More than one after on an item: the newest is the pair,
// the older ones are labelled After and stay in the photos.

const isAfter = (p) => !!p && String(p.kind || '').toLowerCase() === 'after';

/** The item's pair { before, after }, or null. photos in the order added. */
export function photoPair(photos) {
  const list = Array.isArray(photos) ? photos : [];
  for (let i = list.length - 1; i >= 0; i--) {
    const a = list[i];
    if (!isAfter(a) || a.beforePhotoId == null) continue;
    const b = list.find((p) => p && p.id != null && String(p.id) === String(a.beforePhotoId) && !isAfter(p));
    if (b) return { before: b, after: a };
  }
  return null;
}

/** id -> 'before' | 'after' for every photo that is one. */
export function pairRoles(photos) {
  const list = Array.isArray(photos) ? photos : [];
  const ids = new Set(list.filter((p) => p && p.id != null).map((p) => String(p.id)));
  const roles = new Map();
  for (const p of list) {
    if (!isAfter(p) || p.id == null) continue;
    if (p.beforePhotoId == null || !ids.has(String(p.beforePhotoId))) continue;
    roles.set(String(p.id), 'after');
    if (!roles.has(String(p.beforePhotoId))) roles.set(String(p.beforePhotoId), 'before');
  }
  return roles;
}

/** The pair first (before, after), then every other photo in its order. */
export function pairFirst(photos) {
  const list = Array.isArray(photos) ? photos : [];
  const pair = photoPair(list);
  if (!pair) return { pair: null, ordered: list.slice(), rest: list.slice() };
  const rest = list.filter((p) => p !== pair.before && p !== pair.after);
  return { pair, ordered: [pair.before, pair.after, ...rest], rest };
}
